import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchDashboard } from "@/lib/api";
import { StationCard } from "@/components/StationCard";
import { CreepCharacter } from "@/components/CreepCharacter";
import { CreepOrbit } from "@/components/CreepOrbit";
import { CreepPopup } from "@/components/CreepPopup";
import { ShareStreak } from "@/components/ShareStreak";
import { SupportProjectModal } from "@/components/SupportProjectModal";
import { SupportWall } from "@/components/SupportWall";
import { toast } from "sonner";
import {
  loadPet, savePet, applyIdleDecay, rollDailyGreeting,
  petCreep, feedCreep, throwSnowball, sleepCreep,
  derivePose, buildGreeting, ACCESSORY_LABELS,
} from "@/lib/petStats";
import {
  Snowflake, Wind, Cloud, Bell, ChevronDown, X, Volume2, VolumeX,
  RotateCcw, Sparkles, Fish, Wand2, Search, TriangleAlert, Share2, Heart,
} from "lucide-react";

const AUDIO_KEY = "creep.audio.v1";

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pet, setPet] = useState(() => applyIdleDecay(loadPet()));
  const [reactionKey, setReactionKey] = useState(0);
  const [tapCount, setTapCount] = useState(0);
  const [showWeatherSheet, setShowWeatherSheet] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showResorts, setShowResorts] = useState(true);
  const [region, setRegion] = useState("Todas");
  const [q, setQ] = useState("");
  const [audio, setAudio] = useState(() => {
    try { return localStorage.getItem(AUDIO_KEY) === "on"; } catch { return false; }
  });
  const [snowfallOn, setSnowfallOn] = useState(true);
  const [floaters, setFloaters] = useState([]); // {id, text, color, x}
  const [snowballs, setSnowballs] = useState([]); // {id}
  const [tempPose, setTempPose] = useState(null); // "chase" | "shake" | "happy"
  const [showHeart, setShowHeart] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const tapWindowRef = useRef({ count: 0, first: 0 });
  const heartHideTimer = useRef(null);
  const greetedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const resortsRef = useRef(null);
  const poseTimerRef = useRef(null);
  const creepPopupRef = useRef(null);

  // Load dashboard and auto-refresh every 5 min (silent) so datos se
  // mantengan frescos sin depender de recarga manual de página.
  // Cache local en localStorage: si una respuesta viene con más
  // estaciones "no disponibles" que la anterior, priorizamos los datos
  // cacheados para no dejar al usuario con pantalla vacía cuando el
  // backend tiene un hipo transitorio con Meteocat/XEMA.
  useEffect(() => {
    let mounted = true;
    let intervalId;
    const STORAGE_KEY = "creep_last_dashboard";
    const MAX_STALE_MS = 24 * 60 * 60 * 1000; // 24h

    // 1) Hidratar inmediatamente desde localStorage
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed._cachedAt || 0);
        if (parsed._data && age < MAX_STALE_MS) {
          setData(parsed._data);
          setLoading(false);
        }
      }
    } catch { /* ignore corrupt cache */ }

    // Merge por estación: si la nueva respuesta marca una estación
    // como `available: false` pero teníamos datos válidos previos,
    // nos quedamos con los previos (marcados como stale) para que el
    // usuario nunca vea "No disponible" durante un hipo transitorio
    // de Meteocat/XEMA.
    //
    // Además, al hacer merge purgamos cualquier estación cacheada cuyo
    // id ya no aparezca en la respuesta actual (p.ej. Andorra tras
    // eliminarla del backend). Así el usuario nunca ve una tarjeta que
    // al pulsarla daría 404.
    const mergeWithCache = (newData, prevData) => {
      if (!prevData || !newData?.stations) return newData;
      const prevMap = new Map((prevData.stations || []).map((s) => [s.station.id, s]));
      const validIds = new Set(newData.stations.map((s) => s.station.id));
      const stations = newData.stations.map((s) => {
        if (s?.available === false && prevMap.has(s.station.id)) {
          const cached = prevMap.get(s.station.id);
          if (cached?.available !== false) {
            return { ...cached, _stale: true, _staleSince: prevData._fetchedAt || Date.now() };
          }
        }
        return s;
      });
      // Elimina también favoritos/compare que apunten a IDs ya inexistentes
      try {
        const favs = JSON.parse(localStorage.getItem("creep.favorites.v1") || "[]");
        const cleanFavs = favs.filter((id) => validIds.has(id));
        if (cleanFavs.length !== favs.length) {
          localStorage.setItem("creep.favorites.v1", JSON.stringify(cleanFavs));
          window.dispatchEvent(new CustomEvent("creep:favorites", { detail: cleanFavs }));
        }
        const cmp = JSON.parse(localStorage.getItem("creep.compare.v1") || "[]");
        const cleanCmp = cmp.filter((id) => validIds.has(id));
        if (cleanCmp.length !== cmp.length) {
          localStorage.setItem("creep.compare.v1", JSON.stringify(cleanCmp));
          window.dispatchEvent(new CustomEvent("creep:compare", { detail: cleanCmp }));
        }
      } catch { /* ignore */ }
      return { ...newData, stations };
    };

    const load = async (silent = false) => {
      try {
        const d = await fetchDashboard();
        if (!mounted) return;
        setData((prev) => {
          const merged = mergeWithCache(d, prev);
          merged._fetchedAt = Date.now();
          try {
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({ _data: merged, _cachedAt: Date.now() }),
            );
          } catch { /* quota / disabled */ }
          return merged;
        });
      } catch {
        /* mantiene los datos anteriores si falla — nunca pantalla en blanco */
      } finally {
        if (mounted && !silent) setLoading(false);
      }
    };
    load(false);
    intervalId = setInterval(() => load(true), 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") load(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted = false;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Daily greeting once data loaded
  useEffect(() => {
    if (!data || greetedRef.current) return;
    greetedRef.current = true;
    const { isNewDay, state } = rollDailyGreeting(pet);
    if (isNewDay) {
      setPet(savePet(state));
      const unlocked = state.accessories.filter((a) => !pet.accessories.includes(a));
      unlocked.forEach((acc) =>
        toast.success(`Nuevo cosmético desbloqueado: ${ACCESSORY_LABELS[acc]}`, { duration: 4500 }),
      );
    }
    const line = buildGreeting(data, state.streak);
    toast(line, {
      description: state.streak > 1 ? `Racha ${state.streak} días` : undefined,
      icon: <Sparkles size={16} className="text-violet-300" />,
      duration: 6000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const pose = useMemo(() => tempPose || derivePose(pet), [pet, tempPose]);

  const spawnFloater = (text, color) => {
    const id = Math.random().toString(36).slice(2);
    const x = 35 + Math.random() * 30;
    setFloaters((arr) => [...arr, { id, text, color, x }]);
    setTimeout(() => setFloaters((arr) => arr.filter((f) => f.id !== id)), 1200);
  };

  const setPoseFor = (p, ms = 1200) => {
    setTempPose(p);
    if (poseTimerRef.current) clearTimeout(poseTimerRef.current);
    poseTimerRef.current = setTimeout(() => {
      setTempPose(null);
      poseTimerRef.current = null;
    }, ms);
  };

  const playSound = useCallback((type) => {
    if (!audio) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type === "meow" ? "triangle" : type === "purr" ? "sine" : "sawtooth";
      const base = type === "meow" ? 620 : type === "purr" ? 140 : 320;
      o.frequency.setValueAtTime(base, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(base * (type === "meow" ? 0.6 : 1.3), ctx.currentTime + 0.28);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.4);
    } catch { /* silent */ }
  }, [audio]);

  const vibrate = (pattern) => { try { navigator.vibrate?.(pattern); } catch { /* noop */ } };

  const doPet = () => {
    setPet(savePet(petCreep(pet)));
    setReactionKey((k) => k + 1);
    setTapCount((c) => c + 1);
    spawnFloater("+5 ♥", "#D6002F");
    setPoseFor("happy", 900);
    playSound("meow");
    vibrate(15);
  };
  const doFeed = () => {
    setPet(savePet(feedCreep(pet)));
    setReactionKey((k) => k + 1);
    spawnFloater("+12 ⚡", "#8A6BFF");
    setPoseFor("happy", 1100);
    playSound("purr");
    toast("Mrr… gracias por el pescado.", { icon: <Fish size={16} className="text-violet-300" /> });
  };
  const doSnowball = () => {
    setPet(savePet(throwSnowball(pet)));
    setReactionKey((k) => k + 1);
    spawnFloater("+8 ❄", "#8A6BFF");
    // Spawn a flying snowball across the arena
    const id = Math.random().toString(36).slice(2);
    setSnowballs((arr) => [...arr, { id }]);
    setTimeout(() => setSnowballs((arr) => arr.filter((s) => s.id !== id)), 1700);
    setPoseFor("chase", 1600);
    playSound("snow");
    vibrate([10, 40, 20]);
    toast("Bola de nieve lanzada. Creep sale corriendo.", { icon: <Snowflake size={16} className="text-violet-300" /> });
  };
  const doSleep = () => {
    setPet(savePet(sleepCreep(pet)));
    // Force sleep pose for 3s regardless of stats
    setPoseFor("sleep", 3000);
    playSound("purr");
    toast("Creep se acomoda a descansar.", { icon: <Wand2 size={16} className="text-violet-300" /> });
  };
  const doResetPet = () => {
    localStorage.removeItem("creep.pet.v1");
    localStorage.removeItem("creep.pet.lastday.v1");
    setPet(loadPet());
    setShowSettings(false);
    toast("Mascota reiniciada.");
  };
  const toggleAudio = () => {
    const next = !audio;
    setAudio(next);
    try { localStorage.setItem(AUDIO_KEY, next ? "on" : "off"); } catch { /* noop */ }
  };

  // Easter-egg: 5 rapid taps → euphoric shake
  useEffect(() => {
    if (tapCount > 0 && tapCount % 5 === 0) {
      toast("¡Nyaaa! Creep está eufórico.", { icon: <Sparkles size={16} className="text-violet-300" /> });
      setPet((s) => savePet({ ...s, happiness: Math.min(100, s.happiness + 6) }));
      spawnFloater("¡EUFÓRICO!", "#D6002F");
      setPoseFor("shake", 1200);
    }
  }, [tapCount]);

  // Gentle 3-5 tap window → reveal the Support heart above Creep
  useEffect(() => {
    if (tapCount <= 0) return;
    const now = Date.now();
    const w = tapWindowRef.current;
    if (now - w.first > 2500) { w.count = 0; w.first = now; }
    w.count += 1;
    if (w.count >= 3 && !showSupport && !showHeart) {
      setShowHeart(true);
      clearTimeout(heartHideTimer.current);
      heartHideTimer.current = setTimeout(() => setShowHeart(false), 9000);
    }
  }, [tapCount, showSupport, showHeart]);

  // Best station (por si algún panel lo necesita)
  const bestStation = useMemo(() => {
    if (!data?.stations?.length) return null;
    const available = data.stations.filter((s) => s.available !== false);
    return [...available].sort((a, b) => (b?.ski?.score || 0) - (a?.ski?.score || 0))[0] || null;
  }, [data]);

  const openResorts = () => {
    setShowResorts(true);
    setTimeout(() => resortsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const filteredStations = useMemo(() => {
    if (!data?.stations) return [];
    return data.stations.filter((s) => {
      if (region !== "Todas" && s.station.region !== region) return false;
      if (q && !s.station.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, region, q]);

  return (
    <div data-testid="dashboard-page" className="relative">
      {/* Snowfall */}
      {snowfallOn && <Snowfall />}

      {/* --- Top status bar --- */}
      <TopStatus
        data={data}
        pet={pet}
        onWeather={() => setShowWeatherSheet(true)}
      />

      {/* --- Central pet arena --- */}
      <section className="relative mt-4 mb-6" data-testid="pet-arena">
        <div
          className="relative rounded-[32px] overflow-hidden"
          style={{
            background:
              "radial-gradient(600px 400px at 50% 40%, rgba(91,46,255,0.22), transparent 65%), radial-gradient(500px 340px at 20% 90%, rgba(42,18,74,0.5), transparent 70%), #0B0B12",
            border: "1px solid rgba(138,107,255,0.15)",
            boxShadow: "inset 0 0 60px rgba(91,46,255,0.15)",
          }}
        >
          {/* Halo backdrop */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] rounded-full"
              style={{
                background: "radial-gradient(closest-side, rgba(138,107,255,0.10), transparent 70%)",
              }}
            />
          </div>

          <div className="relative h-[520px] sm:h-[560px] md:h-[620px] flex items-center justify-center">
            {/* Orbit */}
            <div className="absolute inset-0 flex items-center justify-center">
              <CreepOrbit
                size={typeof window !== "undefined" && window.innerWidth < 640 ? 340 : 500}
                onWeather={() => setShowWeatherSheet(true)}
                onSettings={() => setShowSettings(true)}
                onResorts={openResorts}
              />
            </div>

            {/* Creep */}
            <div className="relative z-10">
              <CreepCharacter
                size={typeof window !== "undefined" && window.innerWidth < 640 ? 240 : 300}
                pose={pose}
                accessories={pet.accessories || []}
                reactionKey={reactionKey}
                onTap={() => { doPet(); creepPopupRef.current?.next(); }}
              />

              {/* Creep's speech bubble — anchored to the pet, click to interact */}
              <CreepPopup ref={creepPopupRef} side="right" />

              {/* Floating +N indicators */}
              <div className="absolute inset-0 pointer-events-none">
                {floaters.map((f) => (
                  <span
                    key={f.id}
                    className="absolute top-1/3 text-sm font-black creep-float-up"
                    style={{
                      left: `${f.x}%`,
                      color: f.color,
                      textShadow: `0 0 12px ${f.color}, 0 0 24px ${f.color}55`,
                    }}
                    data-testid="floater"
                  >
                    {f.text}
                  </span>
                ))}
              </div>

              {/* Flying snowballs */}
              {snowballs.map((s) => (
                <span
                  key={s.id}
                  data-testid="snowball-fly"
                  className="absolute top-1/2 left-1/2 w-4 h-4 rounded-full creep-snowball pointer-events-none"
                  style={{
                    background: "radial-gradient(closest-side, #FFFFFF, #cfd6ff)",
                    boxShadow: "0 0 14px rgba(255,255,255,0.85), 0 0 28px rgba(138,107,255,0.5)",
                  }}
                />
              ))}

              {/* Floating Support heart — appears after 3-5 gentle taps */}
              {showHeart && (
                <button
                  data-testid="support-heart"
                  onClick={() => { setShowSupport(true); setShowHeart(false); }}
                  className="absolute left-1/2 -translate-x-1/2 top-4 z-30 group"
                  style={{ animation: "creep-heart-pop 0.6s ease-out forwards, creep-heart-bob 2.2s ease-in-out 0.6s infinite" }}
                  aria-label="Apoyar el proyecto"
                >
                  <span
                    className="relative flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-full font-semibold text-[12px] whitespace-nowrap"
                    style={{
                      background: "linear-gradient(180deg, rgba(214,0,47,0.95), rgba(91,46,255,0.9))",
                      color: "white",
                      boxShadow: "0 0 24px rgba(214,0,47,0.6), 0 0 48px rgba(91,46,255,0.4)",
                      border: "1px solid rgba(255,255,255,0.18)",
                    }}
                  >
                    <Heart size={13} fill="currentColor" />
                    <span className="tracking-tight">Apoya el proyecto</span>
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-white/90"
                          style={{ boxShadow: "0 0 8px #fff" }} />
                  </span>
                </button>
              )}

              {/* Name tag */}
              <div className="mt-2 text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.3em] text-white/70"
                  style={{ background: "rgba(11,11,18,0.6)", border: "1px solid rgba(138,107,255,0.25)" }}
                  data-testid="creep-name-tag"
                >
                  <span className="w-1 h-1 rounded-full bg-[#D6002F] animate-pulse" />
                  Creep · {poseLabel(pose)}
                </div>
              </div>
            </div>
          </div>

          {/* Interaction dock */}
          <div className="relative px-3 sm:px-5 pb-4">
            <div
              className="flex items-center justify-around gap-1.5 rounded-2xl px-2 py-1.5"
              style={{ background: "rgba(11,11,18,0.65)", border: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(14px)" }}
              data-testid="interaction-dock"
            >
              <DockButton testId="dock-pet" label="Acariciar" icon={Sparkles} onClick={doPet} />
              <DockButton testId="dock-feed" label="Alimentar" icon={Fish} onClick={doFeed} />
              <DockButton testId="dock-snowball" label="Bola nieve" icon={Snowflake} onClick={doSnowball} />
              <DockButton testId="dock-sleep" label="Dormir" icon={Wand2} onClick={doSleep} />
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <StatBar pet={pet} />
      </section>

      {/* --- Resorts panel (kept accessible, opened via orbit or button) --- */}
      <section ref={resortsRef} className="mb-4" data-testid="resorts-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#8A6BFF]">Estaciones</div>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">Estaciones de Cataluña</h2>
          </div>
          <button
            data-testid="resorts-toggle"
            onClick={() => setShowResorts((s) => !s)}
            className="creep-btn-ghost inline-flex items-center gap-1.5 text-xs !py-1.5 !px-3"
          >
            {showResorts ? "Ocultar" : "Ver todas"}
            <ChevronDown size={14} className={`transition-transform duration-200 ${showResorts ? "rotate-180" : ""}`} />
          </button>
        </div>

        {showResorts && (
          <div className="creep-chip-in">
            {/* Filters */}
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-between">
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="region-filters">
                {["Todas"].map((r) => (
                  <button
                    key={r}
                    data-testid={`region-filter-${r.toLowerCase()}`}
                    onClick={() => setRegion(r)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs transition-colors ${
                      region === r
                        ? "bg-[#5B2EFF]/30 border border-[#8A6BFF]/60 text-white"
                        : "border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <label className="relative sm:w-64">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  data-testid="station-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar estación…"
                  className="w-full pl-8 pr-3 py-2 text-sm rounded-full bg-white/[0.04] border border-white/10 placeholder:text-white/40 focus:outline-none focus:border-[#8A6BFF]/60 focus:bg-white/[0.06]"
                />
              </label>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="creep-glass h-[340px] animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="stations-grid">
                {filteredStations.map((s) => (
                  <StationCard key={s.station.id} data={s} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* --- Public Support Wall --- */}
      <section className="mb-8" data-testid="home-support-wall">
        <SupportWall compact />
      </section>

      {/* --- Alerts strip --- */}
      {data?.alerts?.length > 0 && (
        <section className="mb-8" data-testid="alerts-summary">
          <div className="flex items-center gap-2 mb-2">
            <TriangleAlert size={14} className="text-[#8A6BFF]" />
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">
              {data.alerts.length} alertas activas
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {data.alerts.slice(0, 8).map((a, i) => (
              <div
                key={i}
                data-testid={`alert-chip-${i}`}
                className="shrink-0 min-w-[220px] px-3 py-2 rounded-2xl border text-[11px]"
                style={{
                  background: "rgba(11,11,18,0.55)",
                  borderColor: a.level === "danger" ? "rgba(214,0,47,0.35)" : "rgba(138,107,255,0.3)",
                }}
              >
                <div className="text-[9px] uppercase tracking-widest text-white/50">{a.station_name}</div>
                <div className="font-semibold text-white truncate">{a.title}</div>
                <div className="text-white/60 truncate">{a.message}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- Weather bottom sheet --- */}
      {showWeatherSheet && (
        <WeatherSheet
          station={bestStation}
          data={data}
          onClose={() => setShowWeatherSheet(false)}
        />
      )}

      {/* --- Settings modal --- */}
      {showSettings && (
        <SettingsModal
          audio={audio}
          onToggleAudio={toggleAudio}
          snowfall={snowfallOn}
          onToggleSnowfall={() => setSnowfallOn((s) => !s)}
          onResetPet={doResetPet}
          onClose={() => setShowSettings(false)}
          pet={pet}
        />
      )}

      {/* --- Support Esquiada modal (voluntary donation, never intrusive) --- */}
      <SupportProjectModal open={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
}

// -------------------- Sub-components --------------------

const poseLabel = (p) =>
  p === "happy" ? "feliz" :
  p === "bored" ? "aburrido" :
  p === "sleep" ? "durmiendo" :
  p === "chase" ? "persiguiendo" :
  p === "shake" ? "eufórico" :
  "atento";

const TopStatus = ({ data, pet, onWeather }) => {
  const featured = useMemo(() => {
    if (!data?.stations?.length) return null;
    const available = data.stations.filter((s) => s.available !== false);
    return [...available].sort((a, b) => (b?.ski?.score || 0) - (a?.ski?.score || 0))[0] || null;
  }, [data]);
  const w = featured?.weather;
  const ski = featured?.ski;
  const alerts = data?.alerts?.length || 0;

  // Recomputa el "hace X" cada 30s para que no se quede congelado.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  const freshness = useMemo(() => {
    const ts = data?._fetchedAt || (featured?.updated_at ? new Date(featured.updated_at).getTime() : null);
    if (!ts) return null;
    const diffMs = Date.now() - ts;
    if (diffMs < 60 * 1000) return "ahora";
    const m = Math.floor(diffMs / 60000);
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    return `hace ${h} h`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, featured]);

  return (
    <header
      className="rounded-3xl px-4 py-3 flex items-center gap-3"
      style={{
        background: "linear-gradient(180deg, rgba(11,11,18,0.75), rgba(11,11,18,0.55))",
        border: "1px solid rgba(138,107,255,0.18)",
        backdropFilter: "blur(20px) saturate(140%)",
      }}
      data-testid="top-status"
    >
      <button
        data-testid="weather-summary"
        onClick={onWeather}
        className="flex-1 text-left min-w-0"
      >
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]/80">
          {featured?.station?.name || "Cargando…"}
        </div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <div className="text-2xl font-extrabold text-white leading-none">
            {w?.temperature != null ? Math.round(w.temperature) : "—"}°
          </div>
          <div className="text-[11px] text-white/70 truncate">
            {w?.sky || "—"}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-3 text-[10px] text-white/60">
          <span className="inline-flex items-center gap-1"><Wind size={11} className="text-[#8A6BFF]" /> {w?.wind_speed != null ? `${Math.round(w.wind_speed)}km/h` : "—"}</span>
          <span className="inline-flex items-center gap-1"><Cloud size={11} className="text-[#8A6BFF]" /> {w?.precipitation_probability ?? 0}%</span>
          {ski && (
            <span className={`inline-flex items-center gap-1 font-semibold ${ski.skiable ? "text-[#8A6BFF]" : "text-amber-200/80"}`}>
              Ski {ski.score}
            </span>
          )}
          {freshness && (
            <span
              data-testid="freshness-indicator"
              className="inline-flex items-center gap-1 text-white/45 ml-auto"
              title="Última actualización de datos"
            >
              <span className="w-1 h-1 rounded-full bg-[#8A6BFF]" /> {freshness}
            </span>
          )}
        </div>
      </button>

      <div className="flex items-center gap-2">
        <button
          data-testid="notifications-btn"
          onClick={onWeather}
          className="relative w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/5 transition-colors"
          aria-label="Notificaciones"
        >
          <Bell size={16} className="text-white/85" />
          {alerts > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
              style={{ background: "#D6002F", boxShadow: "0 0 10px rgba(214,0,47,0.65)" }}
            >
              {alerts}
            </span>
          )}
        </button>
      </div>
    </header>
  );
};

const DockButton = ({ label, icon: Icon, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl transition-colors text-white/70 hover:text-white hover:bg-white/5 active:scale-95"
  >
    <Icon size={16} className="text-[#8A6BFF]" />
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

const StatBar = ({ pet }) => {
  const items = [
    { key: "mood",       label: "Ánimo",     value: pet.mood,       color: "#8A6BFF" },
    { key: "energy",     label: "Energía",   value: pet.energy,     color: "#5B2EFF" },
    { key: "happiness",  label: "Felicidad", value: pet.happiness,  color: "#D6002F" },
    { key: "curiosity",  label: "Curiosidad", value: pet.curiosity, color: "#8A6BFF" },
  ];
  return (
    <div
      className="mt-3 grid grid-cols-4 gap-2 rounded-2xl px-3 py-2.5"
      style={{ background: "rgba(11,11,18,0.6)", border: "1px solid rgba(255,255,255,0.05)", backdropFilter: "blur(12px)" }}
      data-testid="stat-bar"
    >
      {items.map((it) => (
        <div key={it.key} data-testid={`stat-${it.key}`}>
          <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-white/50">
            <span>{it.label}</span>
            <span className="text-white/80">{it.value}</span>
          </div>
          <div className="mt-1 h-1 rounded-full bg-white/6 overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${it.value}%`, background: it.color, boxShadow: `0 0 8px ${it.color}80` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const WeatherSheet = ({ station, data, onClose }) => {
  const w = station?.weather;
  const ski = station?.ski;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="weather-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5"
        style={{ background: "linear-gradient(180deg, #14101F, #0B0B12)", border: "1px solid rgba(138,107,255,0.25)" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">Resumen del día</div>
            <div className="font-extrabold text-lg">{station?.station?.name || "—"}</div>
          </div>
          <button data-testid="weather-sheet-close" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MiniStat label="Temp" value={w ? `${Math.round(w.temperature)}°` : "—"} />
          <MiniStat label="Viento" value={w ? `${Math.round(w.wind_speed)}km/h` : "—"} />
          <MiniStat label="Ski hoy" value={ski ? `${ski.score}` : "—"} />
        </div>
        <div className="rounded-2xl p-3 mb-3" style={{ background: "rgba(91,46,255,0.14)", border: "1px solid rgba(138,107,255,0.3)" }}>
          <div className="text-[10px] uppercase tracking-widest text-[#8A6BFF] mb-0.5">
            {ski?.skiable ? "Buen día para esquiar" : "No apto para esquiar"}
          </div>
          <div className="text-sm text-white">
            {ski?.reasons?.[0] || (ski?.skiable ? "Condiciones favorables." : "Condiciones desfavorables hoy.")}
          </div>
        </div>
        {data?.alerts?.length > 0 && (
          <div className="rounded-2xl p-3" style={{ background: "rgba(214,0,47,0.10)", border: "1px solid rgba(214,0,47,0.3)" }}>
            <div className="text-[10px] uppercase tracking-widest text-[#D6002F] mb-0.5">Avisos</div>
            <div className="text-sm text-white">{data.alerts.length} alertas activas — abre el mapa para verlas.</div>
          </div>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
    <div className="text-[9px] uppercase tracking-widest text-white/50">{label}</div>
    <div className="mt-1 text-base font-semibold text-white">{value}</div>
  </div>
);

const SettingsModal = ({ audio, onToggleAudio, snowfall, onToggleSnowfall, onResetPet, onClose, pet }) => (
  <div
    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
    onClick={onClose}
    data-testid="settings-modal"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5"
      style={{ background: "linear-gradient(180deg, #14101F, #0B0B12)", border: "1px solid rgba(138,107,255,0.25)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">Ajustes de Creep</div>
          <div className="font-extrabold text-lg">Preferencias</div>
        </div>
        <button data-testid="settings-close" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70">
          <X size={16} />
        </button>
      </div>

      <div className="space-y-2 mb-4">
        <Row
          testId="setting-audio"
          left={<><span>Sonidos suaves</span><div className="text-[10px] text-white/50">Maullidos y bola de nieve</div></>}
          right={
            <button onClick={onToggleAudio} className="creep-btn-ghost inline-flex items-center gap-2 text-xs !py-1.5 !px-3">
              {audio ? <Volume2 size={14} /> : <VolumeX size={14} />}
              {audio ? "Activado" : "Silencio"}
            </button>
          }
        />
        <Row
          testId="setting-snow"
          left={<><span>Nevada de ambiente</span><div className="text-[10px] text-white/50">Copos flotando en la pantalla</div></>}
          right={
            <button onClick={onToggleSnowfall} className="creep-btn-ghost inline-flex items-center gap-2 text-xs !py-1.5 !px-3">
              <Snowflake size={14} />
              {snowfall ? "Activada" : "Desactivada"}
            </button>
          }
        />
        <Row
          testId="setting-reset"
          left={<><span>Reiniciar mascota</span><div className="text-[10px] text-white/50">Borra stats y racha</div></>}
          right={
            <button onClick={onResetPet} className="creep-btn-ghost inline-flex items-center gap-2 text-xs !py-1.5 !px-3 !text-red-200 !border-red-500/30">
              <RotateCcw size={14} /> Reiniciar
            </button>
          }
        />
      </div>

      <div className="rounded-2xl p-3" style={{ background: "rgba(91,46,255,0.10)", border: "1px solid rgba(138,107,255,0.25)" }}>
        <div className="text-[10px] uppercase tracking-widest text-[#8A6BFF] mb-2">Cosméticos</div>
        <div className="flex flex-wrap gap-1.5">
          {["scarf", "goggles", "snowboard", "cloak"].map((key) => {
            const owned = (pet.accessories || []).includes(key);
            return (
              <span
                key={key}
                data-testid={`cosmetic-${key}`}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
                  owned
                    ? "border-[#8A6BFF]/60 bg-[#5B2EFF]/25 text-white"
                    : "border-white/10 text-white/40"
                }`}
              >
                {ACCESSORY_LABELS[key]}{owned ? "" : " · bloqueado"}
              </span>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="text-[10px] text-white/50">
            Racha actual: <span className="text-white">{pet.streak || 1} días</span>
          </div>
          <ShareStreak pet={pet} />
        </div>
      </div>
    </div>
  </div>
);

const Row = ({ left, right, testId }) => (
  <div
    className="flex items-center justify-between rounded-xl p-3"
    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
    data-testid={testId}
  >
    <div className="text-sm text-white">{left}</div>
    {right}
  </div>
);

const Snowfall = () => {
  const flakes = useMemo(
    () => Array.from({ length: 26 }).map((_, i) => ({
      left: Math.random() * 100,
      size: 2 + Math.random() * 4,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 10,
      opacity: 0.35 + Math.random() * 0.45,
    })),
    [],
  );
  return (
    <div className="fixed inset-0 pointer-events-none z-0" data-testid="snowfall">
      {flakes.map((f, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${f.left}%`,
            top: "-4px",
            width: f.size,
            height: f.size,
            opacity: f.opacity,
            filter: "blur(0.3px)",
            animation: `creep-snowfall ${f.duration}s linear ${f.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
};
